import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { CheckCircle2, ClipboardPenLine, Clock3, LogIn, Upload, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  applicationsApi,
  authApi,
  type ApplicationStatus,
  type MyApplication,
} from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { formatDateTime } from '@/lib/utils';

const ABOUT_MAX_LENGTH = 2_000;
const MINECRAFT_NICKNAME_PATTERN = /^[A-Za-z0-9_]{3,16}$/;
const VIDEO_TYPES = new Set(['video/mp4', 'video/webm', 'video/quicktime', 'video/x-m4v']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov', 'm4v']);

type FormState = {
  name: string;
  nickname: string;
  age: string;
  about: string;
};

const emptyForm = (): FormState => ({ name: '', nickname: '', age: '', about: '' });

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

const statusMeta: Record<
  ApplicationStatus,
  { title: string; description: string; badge: string; variant: 'secondary' | 'success' | 'danger' }
> = {
  pending: {
    title: 'Заявка на рассмотрении',
    description: 'Администрация уже получила вашу заявку. Остаётся дождаться решения.',
    badge: 'На рассмотрении',
    variant: 'secondary',
  },
  accepted: {
    title: 'Заявка принята',
    description: 'Поздравляем! Ваша заявка одобрена администрацией сервера.',
    badge: 'Принята',
    variant: 'success',
  },
  rejected: {
    title: 'Заявка отклонена',
    description: 'Вы можете заполнить форму ещё раз, когда приём заявок открыт.',
    badge: 'Отклонена',
    variant: 'danger',
  },
};

function ApplicationStatusCard({ application }: { application: MyApplication }) {
  const meta = statusMeta[application.status];
  const Icon =
    application.status === 'accepted'
      ? CheckCircle2
      : application.status === 'rejected'
        ? XCircle
        : Clock3;

  return (
    <Card>
      <CardContent className="flex flex-wrap items-start gap-4 pt-6">
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-eol-pink/10">
          <Icon className="h-6 w-6 text-eol-pink" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-bold text-eol-white">{meta.title}</h2>
            <Badge variant={meta.variant}>{meta.badge}</Badge>
          </div>
          <p className="mt-2 text-sm text-eol-desc">{meta.description}</p>
          <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
            <p className="text-eol-desc">
              Никнейм: <span className="font-semibold text-eol-white">{application.nickname}</span>
            </p>
            <p className="text-eol-desc">
              Отправлена: <span className="text-eol-white">{formatDateTime(application.submittedAt)}</span>
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ApplicationsPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const prefilledIdRef = useRef<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [video, setVideo] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const meQuery = useQuery({ queryKey: ['me'], queryFn: authApi.me, retry: false });
  const configQuery = useQuery({
    queryKey: ['applications', 'config'],
    queryFn: applicationsApi.config,
    staleTime: 5_000,
    refetchInterval: 15_000,
  });
  const mineQuery = useQuery({
    queryKey: ['applications', 'mine'],
    queryFn: applicationsApi.mine,
    enabled: Boolean(meQuery.data),
    staleTime: 0,
    refetchInterval: 15_000,
  });

  useEffect(() => {
    const application = mineQuery.data;
    if (application?.status !== 'rejected' || prefilledIdRef.current === application.id) return;
    prefilledIdRef.current = application.id;
    setForm({
      name: application.name ?? '',
      nickname: application.nickname,
      age: String(application.age),
      about: application.about,
    });
  }, [mineQuery.data]);

  const submit = useMutation({
    mutationFn: () => {
      const payload = new FormData();
      payload.append('name', form.name.trim());
      payload.append('nickname', form.nickname.trim());
      payload.append('age', form.age);
      payload.append('about', form.about.trim());
      payload.append('video', video!);
      setUploadProgress(0);
      return applicationsApi.submit(payload, setUploadProgress);
    },
    onSuccess: () => {
      toast.success('Заявка отправлена');
      setForm(emptyForm());
      setVideo(null);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      setUploadProgress(0);
      const code = error.response?.data?.error;
      const messages: Record<string, string> = {
        applications_closed: 'Приём заявок сейчас закрыт',
        no_available_slots: 'Сейчас нет свободных слотов',
        application_pending: 'Ваша заявка уже находится на рассмотрении',
        application_already_accepted: 'Ваша заявка уже принята',
        video_too_large: 'Видео превышает допустимый размер',
        unsupported_video_type: 'Поддерживаются видео MP4, WebM, MOV и M4V',
        invalid_video_file: 'Файл не похож на поддерживаемое видео',
        invalid_application_nickname: 'Minecraft-ник должен содержать от 3 до 16 латинских букв, цифр или символов _',
        invalid_application_fields: 'Проверьте заполнение полей формы',
      };
      toast.error(messages[code ?? ''] ?? error.response?.data?.message ?? 'Не удалось отправить заявку');
      queryClient.invalidateQueries({ queryKey: ['applications', 'config'] });
    },
  });

  const config = configQuery.data;
  const mine = mineQuery.data;
  const age = Number(form.age);
  const nicknameValid = MINECRAFT_NICKNAME_PATTERN.test(form.nickname.trim());
  const formValid =
    nicknameValid &&
    Number.isInteger(age) &&
    age >= 1 &&
    age <= 120 &&
    form.about.trim().length >= 20 &&
    form.about.trim().length <= ABOUT_MAX_LENGTH &&
    Boolean(video);

  function chooseVideo(file: File | null) {
    if (!file) {
      setVideo(null);
      return;
    }
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!VIDEO_TYPES.has(file.type.toLowerCase()) && !VIDEO_EXTENSIONS.has(extension)) {
      toast.error('Поддерживаются видео MP4, WebM, MOV и M4V');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setVideo(null);
      return;
    }
    if (config && file.size > config.maxVideoSizeMb * 1024 * 1024) {
      toast.error(`Максимальный размер видео — ${config.maxVideoSizeMb} МБ`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setVideo(null);
      return;
    }
    setVideo(file);
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!formValid || submit.isPending) return;
    submit.mutate();
  }

  const loading = meQuery.isLoading || configQuery.isLoading || Boolean(meQuery.data && mineQuery.isLoading);

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-6 py-12 lg:px-12">
      <div>
        <div className="flex items-center gap-3">
          <ClipboardPenLine className="h-8 w-8 text-eol-pink" />
          <h1 className="text-3xl font-extrabold text-eol-white md:text-4xl">Заявка на сервер</h1>
        </div>
        <p className="mt-3 max-w-2xl text-eol-desc">
          Расскажите немного о себе и приложите короткое видео. Администрация рассмотрит заявку.
        </p>
      </div>

      {loading ? (
        <p className="text-eol-desc">Загрузка…</p>
      ) : configQuery.isError ? (
        <Card>
          <CardContent className="pt-6 text-eol-desc">Не удалось загрузить настройки заявок.</CardContent>
        </Card>
      ) : !meQuery.data ? (
        <Card>
          <CardContent className="py-10 text-center">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-eol-pink/10">
              <LogIn className="h-7 w-7 text-eol-pink" />
            </div>
            <h2 className="mt-4 text-2xl font-bold text-eol-white">Войдите через Discord</h2>
            <p className="mx-auto mt-2 max-w-md text-eol-desc">
              Подать заявку могут только авторизованные пользователи. После входа вы вернётесь на эту страницу.
            </p>
            <Button asChild className="mt-6">
              <a href={authApi.loginUrl('/applications')}>Войти через Discord</a>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {mine && <ApplicationStatusCard application={mine} />}

          {mine?.status !== 'pending' && mine?.status !== 'accepted' && (
            !config?.canSubmit ? (
              <Card>
                <CardContent className="py-10 text-center">
                  <Clock3 className="mx-auto h-10 w-10 text-eol-pink" />
                  <h2 className="mt-4 text-2xl font-bold text-eol-white">Сейчас нет свободных слотов</h2>
                  <p className="mt-2 text-eol-desc">
                    Приём заявок временно закрыт. Загляните на эту страницу позже.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle>{mine?.status === 'rejected' ? 'Подать заявку повторно' : 'Форма заявки'}</CardTitle>
                      <CardDescription className="mt-1">
                        Все поля, кроме имени, обязательны. Свободных слотов: {config.availableSlots}.
                      </CardDescription>
                    </div>
                    <Badge>{config.availableSlots} из {config.totalSlots} свободно</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <form className="space-y-5" onSubmit={onSubmit}>
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="block">
                        <span className="text-sm font-semibold text-eol-white">Ваше имя</span>
                        <span className="ml-1 text-xs text-eol-desc">необязательно</span>
                        <Input
                          className="mt-2"
                          value={form.name}
                          maxLength={64}
                          onChange={(event) => setForm({ ...form, name: event.target.value })}
                          placeholder="Как к вам обращаться"
                        />
                        <span className="mt-1 block text-right text-xs text-eol-desc">{form.name.length}/64</span>
                      </label>

                      <label className="block">
                        <span className="text-sm font-semibold text-eol-white">Ваш никнейм</span>
                        <Input
                          className="mt-2"
                          value={form.nickname}
                          minLength={3}
                          maxLength={16}
                          pattern="[A-Za-z0-9_]{3,16}"
                          title="От 3 до 16 латинских букв, цифр или символов _"
                          required
                          onChange={(event) => setForm({ ...form, nickname: event.target.value })}
                          placeholder="Minecraft-ник"
                          aria-invalid={Boolean(form.nickname) && !nicknameValid}
                        />
                        <span
                          className={`mt-1 block text-xs ${form.nickname && !nicknameValid ? 'text-eol-red' : 'text-eol-desc'}`}
                        >
                          3–16 латинских букв, цифр или символов _
                          <span className="float-right">{form.nickname.length}/16</span>
                        </span>
                      </label>
                    </div>

                    <label className="block max-w-xs">
                      <span className="text-sm font-semibold text-eol-white">Сколько вам полных лет</span>
                      <Input
                        className="mt-2"
                        value={form.age}
                        inputMode="numeric"
                        maxLength={3}
                        required
                        onChange={(event) =>
                          setForm({ ...form, age: event.target.value.replace(/\D/g, '').slice(0, 3) })
                        }
                        placeholder="Например, 18"
                      />
                    </label>

                    <label className="block">
                      <span className="text-sm font-semibold text-eol-white">Расскажите о себе</span>
                      <textarea
                        rows={7}
                        minLength={20}
                        maxLength={ABOUT_MAX_LENGTH}
                        required
                        className="mt-2 w-full resize-y rounded-md border border-white/10 bg-eol-black/40 px-3 py-2 text-sm leading-6 text-eol-white placeholder:text-eol-desc focus-visible:border-eol-pink/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eol-pink/50"
                        value={form.about}
                        onChange={(event) => setForm({ ...form, about: event.target.value })}
                        placeholder="Коротко расскажите о себе, интересах и опыте игры…"
                      />
                      <span className="mt-1 block text-right text-xs text-eol-desc">
                        {form.about.length}/{ABOUT_MAX_LENGTH}
                      </span>
                    </label>

                    <div>
                      <span className="text-sm font-semibold text-eol-white">Видео о себе</span>
                      <div className="mt-2 rounded-lg border border-dashed border-white/15 bg-eol-black/25 p-4">
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-eol-pink/10">
                            <Upload className="h-5 w-5 text-eol-pink" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-eol-white">
                              {video ? video.name : 'Выберите видеофайл'}
                            </p>
                            <p className="mt-1 text-xs text-eol-desc">
                              MP4, WebM, MOV или M4V · до {config.maxVideoSizeMb} МБ
                              {video ? ` · ${formatBytes(video.size)}` : ''}
                            </p>
                          </div>
                        </div>
                        <Input
                          ref={fileInputRef}
                          type="file"
                          className="mt-4 h-auto py-2 file:mr-3 file:rounded-md file:border-0 file:bg-eol-pink/15 file:px-3 file:py-2 file:font-semibold file:text-eol-pink"
                          accept=".mp4,.webm,.mov,.m4v,video/mp4,video/webm,video/quicktime,video/x-m4v"
                          aria-label="Видео о себе"
                          required
                          onChange={(event) => chooseVideo(event.target.files?.[0] ?? null)}
                        />
                      </div>
                    </div>

                    {submit.isPending && (
                      <div>
                        <div
                          className="h-2 overflow-hidden rounded-full bg-white/5"
                          role="progressbar"
                          aria-label="Загрузка видео"
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={uploadProgress}
                        >
                          <div
                            className="h-full rounded-full bg-eol-pink transition-[width]"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <p className="mt-2 text-xs text-eol-desc">Загрузка видео: {uploadProgress}%</p>
                      </div>
                    )}

                    <Button type="submit" disabled={!formValid || submit.isPending} className="w-full sm:w-auto">
                      {submit.isPending ? 'Отправляем…' : 'Отправить заявку'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )
          )}
        </>
      )}
    </div>
  );
}
